import React, { useContext, useState } from 'react'

import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFDownloadLink,
  Image,
  Font,
  Link,
} from '@react-pdf/renderer'
import moment from 'moment'
import { TenantSettingsContext, UserContext } from '../../../../App'
import RecordSection from './_RecordSection'
import InfoLine from './_InfoLine'
import PatientDetails from './PatientDetails'
import IncidentDetails from './IncidentDetails'
import PastMedicalHistory from './PastMedicalHistory'
import History from './History'
import Assessment from './Assessment'
import VitalSigns from './VitalSigns'
import Treatment from './Treatment'
import Safeguarding from './Safeguarding'
import Plan from './Plan'
import HSIncident from './HSIncident'
import CALNotes from './CALNotes'
import Attachments from './Attachments'
import Welfare from './Welfare'
import LiveTimeline from './LiveTimeline'

const styles = StyleSheet.create({
  pageNumber: {
    position: 'absolute',
    fontSize: 12,
    bottom: 30,
    left: 40,
    right: 0,
    textAlign: 'left',
    color: '#f2f2f2',
  },
  ambuNet: {
    position: 'absolute',
    fontSize: 12,
    bottom: 30,
    left: 0,
    right: 40,
    textAlign: 'right',
    color: '#f2f2f2',
  },
})

const EpcrPDF = ({
  epcr,
  orgSettings,
  onRender,
  printedBy,
  anonymised,
  calNotes,
}) => {
  const [generatePDF, setGeneratePDF] = useState(false)
  const isWelfare = epcr?.mode === 'Welfare'
  const isCriticalCare = epcr?.mode === 'Critical Care'
  const DocumentComponent = () => (
    <Document options={{ isEvalSupported: false }}>
      <Page
        size="A4"
        style={{
          // paddingTop: 10,
          paddingHorizontal: 20,
          paddingBottom: 50,
          fontFamily: 'Helvetica',
        }}
      >
        <View fixed style={{ height: 30 }} />
        <View
          style={{ flexDirection: 'column', gap: 2, paddingBottom: 10 }}
          // wrap={true}
        >
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              position: 'center',
              marginTop: 10,
              paddingHorizontal: 20,
              left: 0,
              right: 0,
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Image src={orgSettings.logo} style={{ height: 30 }} />
            <Text style={{ textAlign: 'right', fontSize: 14, color: 'grey' }}>
              {orgSettings.orgName}
            </Text>
          </View>
          <View
            style={{
              flexDirection: 'column',
              alignItems: 'center',
              marginTop: 14,
              gap: 6,
            }}
          >
            <Text style={{ fontSize: 18, color: '#3c4e50' }}>
              {isWelfare
                ? 'Welfare Record'
                : isCriticalCare
                  ? 'Critical Care Record'
                  : 'Patient Care Record'}
            </Text>
            {!anonymised && (
              <Text style={{ fontSize: 14, color: 'red' }}>CONFIDENTIAL</Text>
            )}
          </View>
          {epcr.review.status === 'Draft' && (
            <View
              style={{
                width: '100%',
                flexDirection: 'column',
                backgroundColor:
                  epcr.review.status === 'Draft' ? '#ff4444' : '#00C851',
                padding: 5,
                borderWidth: '2px',
                borderStyle: 'solid',
                borderColor:
                  epcr.review.status === 'Draft' ? '#CC0000' : '#007E33',
              }}
            >
              <Text
                style={{ fontSize: 14, color: 'white', textAlign: 'center' }}
              >
                {epcr.review.status.toUpperCase()}
              </Text>
            </View>
          )}
          <View
            style={{
              marginVertical: 10,
              marginHorizontal: 20,
              padding: 10,
              borderStyle: 'solid',
              borderWidth: '1px',
            }}
          >
            <Text style={{ fontSize: 8 }}>
              The information contained in this document is privileged and
              confidential and is protected under UK Data Protection laws,
              including but not limited to the Data Protection Act 2018 and the
              Computer Misuse Act 1990. This document contains sensitive
              personal information intended solely for the authorized recipient.
              Any unauthorised access, use, disclosure, or distribution of this
              information is strictly prohibited and may result in legal action.
              If you are not the intended recipient, please notify the sender
              immediately, securely destroy this document, and refrain from
              accessing or using the contents.
            </Text>
          </View>
          {anonymised && (
            <View
              style={{
                marginVertical: 1,
                marginHorizontal: 20,
                padding: 10,
                borderStyle: 'solid',
                borderWidth: '2px',
                borderColor: 'red',
              }}
            >
              <Text style={{ fontSize: 10, color: 'red' }}>
                Disclaimer: This ePCR has been anonymised by AmbuNet at the
                request of the user. While identifying fields (such as name,
                date of birth, address, and contact information) have been
                removed or redacted, AmbuNet cannot guarantee the absence of
                identifiable information in free-text or clinical narrative
                fields. It is the responsibility of the user generating this
                document to ensure compliance with applicable data protection
                legislation before sharing or distributing this report. AmbuNet
                accepts no liability for any breach of confidentiality arising
                from identifiable information included in unexpected fields.
              </Text>
            </View>
          )}
        </View>
        <View style={{ flexDirection: 'column', gap: 20 }}>
          <PatientDetails epcr={epcr} anonymised={anonymised} />
          <IncidentDetails epcr={epcr} anonymised={anonymised} />
          {isWelfare ? (
            <Welfare epcr={epcr} />
          ) : (
            <>
              <PastMedicalHistory epcr={epcr} />
              <History epcr={epcr} />
              <Assessment epcr={epcr} />
              <VitalSigns epcr={epcr} />
              <Treatment epcr={epcr} />
              <LiveTimeline epcr={epcr} />
              <HSIncident epcr={epcr} />
              {!anonymised && <Safeguarding epcr={epcr} />}
              <CALNotes notes={calNotes} />
              <Plan epcr={epcr} printedBy={printedBy} anonymised={anonymised} />
            </>
          )}
          <Attachments epcr={epcr} anonymised={anonymised} />
        </View>
        <View
          style={{
            position: 'absolute',
            bottom: 40,
            left: 20,
            right: 20,
            // textAlign: 'center',
            fontSize: 12,
            color: 'grey',
          }}
          render={({ pageNumber, totalPages }) =>
            pageNumber === totalPages && (
              <View style={{ flexDirection: 'column', gap: 2 }}>
                <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 10 }}>
                  Powered by AmbuNet &#174;
                </Text>
                <Text style={{ fontSize: 10 }}>
                  AmbuNet® provides the technical platform for accessing these
                  records. AmbuNet® is not responsible for the accuracy of the
                  medical information or any decisions made based on the data
                  provided. The responsibility for the correct and authorised
                  use of this record lies with the user and the issuing
                  organisation.
                </Text>
              </View>
            )
          }
        />
      </Page>
    </Document>
  )

  return <DocumentComponent />
}

export default EpcrPDF
